import { useTranslation } from "react-i18next";
import useEvent from "react-use-event-hook";

import {
  BookingApi,
  DialogType,
  IExtendableDto,
  ISearchCriteria,
  ReportsApi,
  RidesApi,
  SearchQueryForOptimize,
  searchQueryOptimizeRestrictions,
  useDialogs,
} from "@ryde/shared";

const apiPerPage: Record<
  SearchQueryForOptimize,
  (args: ISearchCriteria<IExtendableDto>) => Promise<number>
> = {
  rides: RidesApi.count,
  bookings: BookingApi.count,
  bookingsPassengers: BookingApi.bookingsCalendarPassengersCount,
  reports: ReportsApi.count,
} as const;

interface IUseIsFetchAllowedArguments {
  searchQueryToOptimize?: SearchQueryForOptimize;
}

export const useIsFetchAllowed = ({
  searchQueryToOptimize,
}: IUseIsFetchAllowedArguments): {
  getIsFetchAllowed: (
    body: ISearchCriteria<IExtendableDto>,
  ) => Promise<boolean>;
} => {
  const { openDialog } = useDialogs();
  const { t } = useTranslation();

  const getIsFetchAllowed = useEvent(
    async (body: ISearchCriteria<IExtendableDto>): Promise<boolean> => {
      if (searchQueryToOptimize === undefined) {
        return true;
      }

      const count = await apiPerPage[searchQueryToOptimize](body);

      if (count < searchQueryOptimizeRestrictions[searchQueryToOptimize]) {
        return true;
      }

      openDialog({
        dialogName: DialogType.Info,
        dialogProps: {
          title: t("general.THIS_PAGE_CONTAINS_LOT_OF_DATA"),
          text: t("general.RECOMMEND_USE_FILTERS_IMPROVE"),
        },
      });

      return false;
    },
  );

  return { getIsFetchAllowed };
};
